<template>
  <div :id="makeSpecialId" class="container-average-image">
    <div v-for="subject in subjects" :key="subject._id" :data-key="subject._id" class="subject-row">
      <div class="subject-label">{{ subject.label }}</div>
      <div class="progress-bar">
        <div
          v-for="(response, index) in subject.responses"
          :key="response.optionId"
          class="progress-segment"
          :style="{
            width: `${getOptionPercentage(subject, response.optionId)}%`,
            backgroundColor: barColors[index]
          }"
          @mouseover="showTooltip($event, response, subject)"
          @mouseleave="hideTooltip"
        >
          <div class="progress-content">
            <img v-if="response.urlImage && getOptionPercentage(subject, response.optionId) >= 15" :src="response.urlImage" class="option-image" :alt="response.value">
            <span class="percentage">{{ getOptionPercentage(subject, response.optionId) }}%</span>
          </div>
        </div>
      </div>
    </div>
    <div class="tooltip" ref="tooltip" :style="tooltipStyle">
      <template v-if="tooltipData">
        <div class="tooltip-content">
          <img v-if="tooltipData.image" :src="tooltipData.image" class="tooltip-image" :alt="tooltipData.option" :style="{ borderColor: tooltipData.color }">
          <div class="tooltip-info">
            <div class="tooltip-option">{{ tooltipData.option }}</div>
            <div class="tooltip-count">{{ tooltipData.count }} {{ $t('components.average_image.responses') }}</div>
            <div class="tooltip-percentage">{{ tooltipData.percentage }}%</div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import gsap from 'gsap'
import { colorsDefault, canvasMaker } from '@/_helpers'

export default {
  name: 'AverageImage',
  props: {
    cardData: {
      type: Array,
      required: false
    },
    question: {
      type: Object,
      required: false
    }
  },
  data () {
    return {
      subjects: [],
      tooltipData: null,
      tooltipStyle: {
        display: 'none',
        left: '0px',
        top: '0px'
      },
      animatedValues: {}
    }
  },
  computed: {
    barColors () {
      if (this.cardData?.[0]?.colors) {
        return this.cardData[0].colors
      }
      const totalOptions = this.subjects?.[0]?.responses?.length || 4
      return colorsDefault.getColorsDefault(totalOptions)
    },
    makeSpecialId () { return canvasMaker.makeCanvasIdOnlyNumbers() }
  },
  mounted () {
    this.formatData()
    this.$nextTick(() => {
      this.animateProgressBars()
    })
  },
  methods: {
    isValidData () {
      return this.cardData && this.cardData[0]?.questions
    },
    formatData () {
      if (!this.isValidData()) return
      const questionData = this.findQuestionData()
      if (!questionData) return
      this.subjects = this.formatSubjectsWithResponses(questionData.data)
    },
    findQuestionData () {
      return this.cardData[0].questions.find(dataQuestion =>
        dataQuestion.question && dataQuestion.question._id === this.question._id
      )
    },
    formatSubjectsWithResponses (responseData) {
      return this.question.subjects.map(subject => {
        const formattedSubject = {
          _id: subject._id,
          label: subject.label,
          options: subject.options.map(option => ({
            ...option,
            value: option.value || option.label
          }))
        }

        formattedSubject.responses = this.initializeResponses(subject.options, responseData?.[subject._id])
        this.mapImageUrlsToResponses(formattedSubject)

        return formattedSubject
      })
    },
    initializeResponses (options, responseData) {
      const responses = options.map(option => ({
        optionId: option._id,
        count: 0,
        value: option.value
      }))

      if (responseData) {
        Object.values(responseData).forEach(response => {
          const existingResponse = responses.find(r => r.optionId === response.optionId)
          if (existingResponse) {
            existingResponse.count = response.qty || 0
          }
        })
      }

      return responses
    },
    mapImageUrlsToResponses (subject) {
      const optionsMap = new Map(
        subject.options.map(option => [option._id, option.label])
      )
      subject.responses.forEach(response => {
        response.urlImage = optionsMap.get(response.optionId)
      })
    },
    getOptionResponses (subject, optionId) {
      return subject.responses.find(response => response.optionId === optionId)?.count || 0
    },
    getTotalResponsesForSubject (subject) {
      return subject.responses.reduce((total, response) => total + response.count, 0)
    },
    getOptionPercentage (subject, optionId) {
      const totalSubjectResponses = this.getTotalResponsesForSubject(subject)
      if (!totalSubjectResponses) return 0
      const responses = this.getOptionResponses(subject, optionId)
      return Math.round((responses / totalSubjectResponses) * 100)
    },
    showTooltip (event, response, subject) {
      const option = subject.options.find(opt => opt._id === response.optionId)
      const index = subject.responses.findIndex(r => r.optionId === response.optionId)

      this.tooltipData = {
        option: option.value,
        count: response.count,
        percentage: this.getOptionPercentage(subject, response.optionId),
        image: response.urlImage,
        color: this.barColors[index]
      }
      this.tooltipStyle = {
        display: 'block',
        left: `${event.clientX + 10}px`,
        top: `${event.clientY + 10}px`
      }
    },
    hideTooltip () {
      this.tooltipStyle.display = 'none'
    },
    animateProgressBars () {
      const containerId = this.makeSpecialId
      const subjects = document.querySelectorAll(`#${containerId} .subject-row`)
      const mainTimeline = gsap.timeline()

      subjects.forEach(subject => {
        const bars = subject.querySelectorAll('.progress-segment')
        const subjectTimeline = gsap.timeline()

        bars.forEach((bar, index) => {
          const width = bar.style.width
          const percentage = parseInt(width)
          const percentageSpan = bar.querySelector('.percentage')
          const uniqueId = `${containerId}-${subject.dataset.key}-${index}`

          this.$set(this.animatedValues, uniqueId, 0)
          const barTimeline = gsap.timeline()

          barTimeline
            .fromTo(bar,
              { width: '0%', opacity: 0 },
              { width, opacity: 1, duration: 0.5, ease: 'sine.inOut' }
            )
            .to(this.animatedValues, {
              [uniqueId]: percentage,
              duration: 0.5,
              ease: 'sine.inOut',
              onUpdate: () => {
                if (percentageSpan) {
                  percentageSpan.textContent = `${Math.round(this.animatedValues[uniqueId])}%`
                }
              }
            }, '<')

          subjectTimeline.add(barTimeline, index * 0.35)
        })

        mainTimeline.add(subjectTimeline, 0)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.container-average-image {
  padding: 15px;
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 8px;
    &-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    &-thumb {
      background: #ccc;
      border-radius: 4px;
      &:hover { background: #bbb; }
    }
  }
}

.subject-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  width: 100%;
  padding: 16px 20px;
  background: linear-gradient(to right, #f8f9ff, #ffffff);
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.06);
  transition: all 0.2s ease-in-out;
  cursor: default;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 6px 12px rgba(0,0,0,0.1);
  }
}

.subject-label {
  width: 30%;
  font-size: 2vh;
  color: #1a1a1a;
  padding-right: 20px;
  line-height: 1.3;
  white-space: pre-wrap;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  letter-spacing: -0.02em;
  text-shadow: 0 1px 1px rgba(0,0,0,0.03);
}

.progress-bar {
  width: 70%;
  height: 32px;
  display: flex;
  border-radius: 16px;
  overflow: hidden;
  background-color: #e9ecef;
  border: 1px solid rgba(0,0,0,0.1);
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.08);
}

.progress-segment {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: white;
  font-size: 1.8vh;
  font-weight: 500;
  position: relative;
  min-width: 0;
  overflow: hidden;
  will-change: width;
  transition: filter 0.2s ease;

  &:hover { filter: brightness(1.1); }

  .progress-content {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 8px;
    position: relative;
    min-width: max-content;
  }

  .option-image {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    display: inline-flex;
  }

  &[style*="width: 1"] .option-image { display: none; }

  .percentage {
    font-weight: 600;
    font-size: 1.8vh;
    text-shadow: 0 1px 1px rgba(0,0,0,0.2);
    white-space: nowrap;
  }
}

.tooltip {
  position: fixed;
  background: #ffffff;
  color: #333;
  padding: 12px;
  border-radius: 12px;
  font-size: 1.8vh;
  pointer-events: none;
  z-index: 1000;
  border: 1px solid rgba(0,0,0,0.1);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  min-width: 220px;
  &-content {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  &-image {
    width: 56px;
    height: 56px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
  }

  &-info {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 4px;
    overflow: hidden;
  }

  &-option {
    font-weight: 700;
    font-size: 2vh;
    color: #111;
    white-space: normal;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    max-width: 200px;
    line-height: 1.4;
  }

  &-count, &-percentage {
    font-size: 1.6vh;
    color: #666;
    font-weight: 500;
  }
}
</style>
